import React, { useState, useEffect, useRef, MouseEvent } from "react";
import styles from "./styles.module.scss";
import EditPricing from "./Modals/AddPricing/EditPricing";
import RemovePricing from "./Modals/RemovePricing";
import DeleteMenu from "../../../../public/images/delete-icon.svg";
import EditMenu from "../../../../public/images/edit-menu-icon.svg";
import ActivateMenu from "../../../../public/images/toggle.svg";
import reorderIcon from "../../../../public/images/ico_reorder.svg";
import ToolTip from "../ToolTip";
import {
  ScheduleResourceResponse,
  ResourceCategoryResponse,
} from "app/utils/request/scheduling/facilityResources";
import { cls } from "app/utils";
import { useTranslation } from 'react-i18next';
import OnlinePayment from "../../../../public/images/online-payment.svg";
import OfflinePayment from "../../../../public/images/offline-payment.svg";
import { TimeTable } from 'app/routes/Scheduling/Pricing/TimeTableSelector';
import { useWindowWidth } from "app/utils/hooks/useWindowWidth";
import { formatDurationLabel } from "app/utils/timeUtils";
import ExpandableDescription from "app/components/ExpandableDescription";
import CalendarColorDot from "app/components/CalendarColorDot";

interface PricingItem {
  id: string | number;
  rate: number | string;
  defaultRate: boolean;
  location: string;
  capacity: number | string;
  duration: string;
  title: string;
  _destroy?: boolean;
  active: boolean;
  collectType?: "online" | "offline" | "both" | "only_package";
  timeTables?: TimeTable[];
  bookingsLimit?: number | string | null;
  description?: string;
  [key: string]: any;
}

interface PricingViewProps {
  list: PricingItem[];
  onEdit: (item: PricingItem) => void;
  onRemove: (item: PricingItem) => void;
  titles: string[];
  coachCurrencySymbol: string;
  onChangeStatus: (item: PricingItem) => void;
  saveNewStatus: (item: PricingItem) => void;
  timeTables?: TimeTable[];
  showBookingsLimit?: boolean;
  stripeIntegrationEnabled?: boolean;
  resources?: ScheduleResourceResponse[];
  categories?: ResourceCategoryResponse[];
  resourcesEnabled?: boolean;
  isReorderMode?: boolean;
  dragHandleProps?: any;
}

const PricingView: React.FC<PricingViewProps> = ({
  list,
  onEdit,
  onRemove,
  titles,
  coachCurrencySymbol,
  onChangeStatus,
  saveNewStatus,
  timeTables,
  showBookingsLimit = false,
  stripeIntegrationEnabled,
  resources = [],
  categories = [],
  resourcesEnabled = false,
  isReorderMode = false,
  dragHandleProps,
}) => {
  const { t } = useTranslation();
  const [menuVisible, setMenuVisible] = useState<number | null>(null);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [removeModalOpen, setRemoveModalOpen] = useState<boolean>(false);
  const [currentItem, setCurrentItem] = useState<PricingItem | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const [shouldShowTooltip, setShouldShowTooltip] = useState({});
  const windowWidth = useWindowWidth();

  const handleArrowClick = (index: number) => {
    setMenuVisible(menuVisible === index ? null : index);
  };

  const handleEdit = (item: PricingItem) => {
    setCurrentItem(item);
    setEditModalOpen(true);
    setMenuVisible(null);
  };

  const handleRemove = (item: PricingItem) => {
    setCurrentItem(item);
    setRemoveModalOpen(true);
    setMenuVisible(null);
  };

  const handleChangeStatus = (item: PricingItem) => {
    if (item.defaultRate === true && item.active === true) {
      const count = list.filter((rate) => rate.active === true).length;

      if (count === 1) {
        onChangeStatus(item);
      } else {
        onChangeStatus(null);
      }
    } else {
      item.active = !item.active;
      saveNewStatus(item);
    }

    setMenuVisible(null);
  };

  const handleConfirmRemove = () => {
    if (currentItem) {
      onRemove(currentItem);
      setRemoveModalOpen(false);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      actionMenuRef.current &&
      !actionMenuRef.current.contains(event.target as Node)
    ) {
      setMenuVisible(null);
    }
  };

  useEffect(() => {
    document.addEventListener(
      "mousedown",
      handleClickOutside as unknown as EventListener,
    );
    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside as unknown as EventListener,
      );
    };
  }, []);

  const checkTextWidth = () => {
    const textMaxWidth = windowWidth >= 1150 ? 150 : 100;

    const visibility = {};
    list.forEach((_, index) => {
      const element = document.getElementById(`title-${index}`);
      if (element) {
        visibility[index] = element.scrollWidth > textMaxWidth;
      }
    });
    setShouldShowTooltip(visibility);
  };

  useEffect(() => {
    checkTextWidth();
  }, [list]);

  const getDurationLabel = (value: string) => formatDurationLabel(value);

  return (
    <>
      {list &&
        list.map(
          (item, index) =>
            !item._destroy && (
              <div
                key={index}
                className={isReorderMode ? styles.rowPricingViewReorder : styles.rowPricingView}
                data-testid={`pricing-row-${item.id}`}
              >
                {isReorderMode && (
                  <div className={styles.drag_handle} {...dragHandleProps}>
                    <img
                      src={reorderIcon}
                      alt="reorder"
                      className={styles.reorder_icon}
                    />
                  </div>
                )}
                <div className={styles.cell}>
                  <div
                    className={`${styles.textWithTooltip} ${item.defaultRate ? styles.defaultRateContainer : ""
                      }`}
                  >
                    <div className={styles.titleRow}>
                      {/* The colour the calendar draws this option's sessions in. */}
                      <CalendarColorDot color={item.color} testId={`pricing-color-${index}`} />
                      <div
                        id={`title-${index}`}
                        className={styles.truncatedText}
                        title={item.title}
                      >
                        {item.title}
                      </div>
                      {shouldShowTooltip[index] && (
                        <div className={styles.toolTipPosition}>
                          <ToolTip selected={true} text={item.title} orientation={'right'} />
                        </div>
                      )}
                    </div>
                  </div>
                  <ExpandableDescription text={item.description} />
                </div>
                <div className={styles.cell}>{getDurationLabel(item.duration)}</div>
                <div className={styles.cell}>
                  {item.rate !== 0 ? `${coachCurrencySymbol}${item.rate}` : ""}
                </div>
                <div className={styles.cell}>
                  <div className={styles.truncatedLocation} title={item.location}>
                    {item.location}
                  </div>
                </div>

                <div className={styles.cell}>
                  <div
                    className={cls({
                      [styles.activeButtonDesign]: item.active,
                      [styles.inactiveButtonDesign]: !item.active
                    })}
                  >
                    {item.active ? t("Active") : t("Inactive")}
                  </div>
                </div>
                <div className={`${styles.cell} ${styles.hide_on_desktop}`} />

                <div className={`${styles.cell} ${styles.payment_cell}`}>
                  <div className={styles.collect_icons}>
                    {(item.collectType === "online" || item.collectType === "both") && (
                      <div className={styles.iconWithTooltip}>
                        <img src={OnlinePayment} alt="Online Payment icon" className={styles.moneyIcon} />
                        <span className={styles.tooltipText}>{t("Collect Online")}</span>
                      </div>
                    )}
                    {(item.collectType === "offline" || item.collectType === "both") && (
                      <div className={styles.iconWithTooltip}>
                        <img src={OfflinePayment} alt="Offline Payment icon" className={styles.moneyIcon} />
                        <span className={styles.tooltipText}>{t("Collect Offline")}</span>
                      </div>
                    )}
                    {item.collectType === "only_package" && (
                      <div className={styles.iconWithTooltip}>
                        <i className={`ico ico-dollar-circle ${styles.moneyIcon}`} />
                        <span className={styles.tooltipText}>{t("Package Only")}</span>
                      </div>
                    )}
                  </div>
                </div>

                {!isReorderMode && (
                  <div className={`${styles.cell} ${styles.iconContainer}`}>
                    <div className={cls("bullets", styles.cursorPointer)} onClick={() => handleArrowClick(index)} data-testid="booking-menu-button" />
                    {menuVisible === index && (
                      <div ref={actionMenuRef} className={styles.actionMenu}>
                        <button
                          onClick={() => handleChangeStatus(item)}
                          className={styles.editButton}
                          data-testid="booking-toggle-status-button"
                        >
                          <img src={ActivateMenu} alt="activateMenu" />
                          <div style={{ marginLeft: "10px" }}>
                            {item.active ? t("Deactivate") : t("Activate")}
                          </div>
                        </button>
                        <button
                          onClick={() => handleEdit(item)}
                          className={styles.editButton}
                          data-testid="booking-edit-button"
                        >
                          <img src={EditMenu} alt="editMenu" />
                          <div style={{ marginLeft: "10px" }}>{t("Edit")}</div>
                        </button>
                        <button
                          onClick={() => handleRemove(item)}
                          className={styles.removeButton}
                          data-testid="booking-delete-button"
                        >
                          <img
                            src={DeleteMenu}
                            alt="deleteMenu"
                            className={styles.deleteImage}
                          />
                          <div style={{ marginLeft: "10px" }}>{t("Delete")}</div>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ),
        )}
      {editModalOpen && currentItem && (
        <EditPricing
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          item={currentItem}
          onEdit={onEdit}
          titles={titles}
          coachCurrencySymbol={coachCurrencySymbol}
          timeTables={timeTables}
          showBookingsLimit={showBookingsLimit}
          stripeIntegrationEnabled={stripeIntegrationEnabled}
          resources={resources}
          categories={categories}
          resourcesEnabled={resourcesEnabled}
        />
      )}
      {removeModalOpen && currentItem && (
        <RemovePricing
          isOpen={removeModalOpen}
          onClose={() => setRemoveModalOpen(false)}
          onDelete={handleConfirmRemove}
          title={currentItem.title}
        />
      )}
    </>
  );
};

export default PricingView;
